import React, { useState, useEffect, useRef } from "react";
import { auth } from "../firebase/firebaseconfig";
import { db } from "../firebase/firebaseconfig";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  addDoc,
} from "firebase/firestore";
import { Link, useParams } from "react-router-dom";
import { useGlobal } from "../App";
import { getBestCategoryDiscount } from "../utils/discounts";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { FaRegHeart } from "react-icons/fa";
import { addToWishlist } from "../GlobalStates/WishlistService";

const slugify = (str) =>
  (str || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

export default function ProductPage() {
  const { slug } = useParams();
  const { products, coupons } = useGlobal();
  const [product, setProduct] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const user = auth.currentUser;

  const { showNotification } = useGlobal();

  // Click tracking function - defined early to avoid ESLint issues
  const trackProductClick = async (product) => {
    console.log("🔥 trackProductClick called for product:", product.name);
    try {
      const clickData = {
        productName: product.name,
        productId: product.id,
        username: user ? user.email : "Anonymous",
        timestamp: new Date().toISOString(),
        page: window.location.pathname
      };

      console.log("📝 Attempting to add click data:", clickData);
      await addDoc(collection(db, "clicks"), clickData);
      console.log("✅ Click tracked successfully:", clickData);
    } catch (error) {
      console.error("❌ Error tracking click:", error);
      console.error("Error details:", error.message);
    }
  };

  const isFrame = product?.category?.toLowerCase().trim() === "frames";
  const isOutOfStock = product?.inStock === false;
  console.log("[ProductPage] isFrame:", isFrame, "Category:", product?.category);

  const getFramePrice = (item, size) => {
    if (!item) return 0;
    let price = 0;
    if (size === "20x30") {
      price = item.smallframeprice ?? item.smallFrameprice ?? item.price ?? 0;
    } else if (size === "30x40") {
      price = item.bigframeprice ?? item.bigFrameprice ?? item.largeframeprice ?? item.price ?? 0;
    } else {
      price = item.price ?? 0;
    }
    const numPrice = Number(price);
    console.log("[getFramePrice]", { item: item.name, size, price, numPrice });
    return numPrice || 0;
  };

  // ====== SCROLL TO TOP ON PRODUCT LOAD ======
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // ====== LOAD PRODUCT FROM SLUG ======
  useEffect(() => {
    if (!products || products.length === 0) {
      setLoading(true);
      return;
    }

    const normalizedSlug = (slug || "").toLowerCase();
    console.log("[ProductPage] Looking for product with slug:", normalizedSlug);
    
    const matchedProduct = products.find((item) => {
      const itemSlug = slugify(item.name);
      const match = itemSlug === normalizedSlug;
      if (match) console.log("[ProductPage] Found product:", item.name, item);
      return match;
    });

    if (matchedProduct) {
      // Normalize category by trimming spaces
      matchedProduct.category = (matchedProduct.category || "").trim();
      console.log("[ProductPage] Product loaded - Category:", matchedProduct.category);
    }
    
    setProduct(matchedProduct || null);
    setLoading(false);
  }, [products, slug]);

  // ====== RESET SELECTIONS WHEN PRODUCT CHANGES ======
  useEffect(() => {
    setSelectedSize(null);
    setQuantity(1);
  }, [slug]);

  /* ================= Add To Cart ================= */
  const addToCart = async () => {
    console.log("[addToCart] Started", { product: product?.name, isFrame, selectedSize, quantity });
    
    if (!product) {
      showNotification("Product not loaded yet");
      return;
    }

    if (isOutOfStock) {
      showNotification("Out of stock");
      return;
    }

    if (isFrame && !selectedSize) {
      showNotification("Please select a size");
      return;
    }

    const finalPrice = isFrame
      ? getFramePrice(product, selectedSize)
      : Number(product.price || 0);

    console.log("[addToCart] finalPrice:", finalPrice, "Type:", typeof finalPrice);

    if (finalPrice <= 0 || isNaN(finalPrice)) {
      showNotification("Price not available");
      return;
    }

    // ✅ Store only productId, quantity, size (Best Practice)
    const cartItem = {
      productId: product.id,
      quantity: quantity,
      size: selectedSize || null,
    };
    
    console.log("[addToCart] cartItem prepared:", cartItem);

    /* ===== Logged User (Firebase) ===== */
    if (user?.email) {
      try {
        const q = query(
          collection(db, "Users"),
          where("User", "==", user.email)
        );

        const snapshot = await getDocs(q);
        if (snapshot.empty) {
          showNotification("User profile not found");
          return;
        }

        const userDoc = snapshot.docs[0];
        const userRef = doc(db, "Users", userDoc.id);
        const userData = userDoc.data();
        
        console.log("[addToCart] User found:", user.email, "Current cart:", userData.cart?.length || 0);

        let updatedCart = Array.isArray(userData.cart) ? [...userData.cart] : [];

        const existingIndex = updatedCart.findIndex(
          item =>
            item.productId === cartItem.productId &&
            item.size === cartItem.size
        );

        if (existingIndex !== -1) {
          updatedCart[existingIndex].quantity += cartItem.quantity;
        } else {
          updatedCart.push(cartItem);
        }

        await updateDoc(userRef, {
          cart: updatedCart,
        });

        console.log("[addToCart] Success - Firebase", cartItem);
        showNotification(`Added ${quantity} to cart!`);
        setQuantity(1); // Reset quantity after adding
      } catch (error) {
        console.error("[addToCart] Error - Firebase:", error);
        showNotification("Error adding to cart");
      }
    } 
    /* ===== Guest User (LocalStorage) ===== */
    else {
      try {
        let cartLS = JSON.parse(localStorage.getItem("cart")) || [];

        const existingIndex = cartLS.findIndex(
          item =>
            item.productId === cartItem.productId &&
            item.size === cartItem.size
        );

        if (existingIndex !== -1) {
          cartLS[existingIndex].quantity += cartItem.quantity;
        } else {
          cartLS.push(cartItem);
        }

        localStorage.setItem("cart", JSON.stringify(cartLS));
        console.log("[addToCart] Success - LocalStorage", cartItem);
        showNotification(`Added ${quantity} to cart!`);
        setQuantity(1); // Reset quantity after adding
      } catch (error) {
        console.error("[addToCart] Error - LocalStorage:", error);
        showNotification("Error adding to cart");
      }
    }
  };

  const addToWishlistHandler = async () => {
    try {
      await addToWishlist(product, user);
      showNotification("Item added to wishlist");
    } catch (err) {
      console.error("[addToWishlistHandler]", err);
      showNotification("Error adding to wishlist");
    }
  };

  // ✅ إضافة loading state
  if (loading) {
    return <div className="text-center mt-5"><h3>Loading...</h3></div>;
  }

  if (!product) {
    return <div className="text-center mt-5"><h3>Product not found</h3></div>;
  }

  return (
    <div className="page-content">
      <section className="mainsection productpage">
        <div className="d-flex gap-5 align-items-start pt-5 productpage-layout">
          {/* Image */}
          <div
            className="imgcontainer d-flex align-items-center justify-content-center"
            style={{ minWidth: "50%" }}
          >
            <img
              src={product.imageUrl || product.image}
              alt={product.name}
              style={{ maxWidth: "80%" }}
            />
          </div>

          {/* Details */}
          <div className="poductdetailscontainer px-5 product-details-sticky">
            <p style={{ fontWeight: 500, color: "#7a7a7a" }} className="m-0">
              GedarStore / {product.category}
            </p>

            <p className="fs-1 m-0">{product.name}</p>
            {isOutOfStock && (
              <span className="badge bg-danger" style={{ marginBottom: "8px" }}>
                Out of stock
              </span>
            )}

            {/* Price */}
            <p className="fs-3 m-0" style={{ color: "#ff6344" }}>
              {(() => {
                if (isFrame && !selectedSize) return "Choose size to see price";

                const basePrice = isFrame
                  ? getFramePrice(product, selectedSize)
                  : Number(product.price || 0);
                const discount = getBestCategoryDiscount(basePrice, coupons, product.category);

                if (discount.discount > 0) {
                  return (
                    <>
                      L.E. {Math.round(discount.finalPrice)}
                      <span
                        style={{
                          marginLeft: "10px",
                          textDecoration: "line-through",
                          color: "#888",
                          fontSize: "0.9rem",
                        }}
                      >
                        L.E. {Math.round(basePrice)}
                      </span>
                    </>
                  );
                }

                return `L.E. ${basePrice || "N/A"}`;
              })()}
            </p>

            {/* Description */}
            <div className="mt-5 mb-5">
              <h5 className="fw-bold mb-3" style={{color: "#333"}}>Product Description</h5>
              <p style={{
                lineHeight: "1",
                color: "#555",
                fontSize: "1rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word"
              }}>
                {product.description || "No description available"}
              </p>
            </div>

            {/* Sizes */}
            {isFrame && (
              <div className="mt-4">
                <p className="fw-bold mb-2">Choose size:</p>

                <div className="d-flex gap-2">
                  <button
                    className={`btn fw-bold ${
                      selectedSize === "20x30"
                        ? "btn-dark"
                        : "btn-outline-dark"
                    }`}
                    style={
                      selectedSize === "20x30"
                        ? {
                            backgroundColor: "#1f2730",
                            color: "#f2d5bd",
                            border: "2px solid #1f2730"
                          }
                        : {
                            color: "#1f2730",
                            borderColor: "#1f2730"
                          }
                    }
                    onClick={() => {
                      setSelectedSize("20x30");
                    }}
                  >
                    20 × 30
                  </button>

                  <button
                    className={`btn fw-bold ${
                      selectedSize === "30x40"
                        ? "btn-dark"
                        : "btn-outline-dark"
                    }`}
                    style={
                      selectedSize === "30x40"
                        ? {
                            backgroundColor: "#1f2730",
                            color: "#f2d5bd",
                            border: "2px solid #1f2730"
                          }
                        : {
                            color: "#1f2730",
                            borderColor: "#1f2730"
                          }
                    }
                    onClick={() => {
                      setSelectedSize("30x40");
                    }}
                  >
                    30 × 40
                  </button>
                </div>
              </div>
            )}

            {selectedSize && (
              <p
                style={{
                  fontSize: "14px",
                  color: "#006eb5",
                  marginTop: "10px",
                }}
              >
                Selected size: {selectedSize}
              </p>
            )}

            {/* Quantity */}
            <div className="mb-3 mt-4">
              <label className="form-label fw-bold">Quantity:</label>
              <div style={{display: "flex", alignItems: "center", gap: "10px"}}>
                <button 
                  className="btn btn-outline-secondary"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  style={{width: "40px", height: "40px", padding: "0"}}
                >
                  −
                </button>
                <input
                  type="number"
                  min="1"
                  className="form-control text-center"
                  style={{ width: "80px" }}
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                />
                <button 
                  className="btn btn-outline-secondary"
                  onClick={() => setQuantity(quantity + 1)}
                  style={{width: "40px", height: "40px", padding: "0"}}
                >
                  +
                </button>
              </div>
            </div>

            {/* Add to cart + Wishlist */}
            <div className="w-100 mt-5 buttonscontainer">
              <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                <button
                  className="w-100 fw-bold"
                  style={{
                    flex: 1,
                    backgroundColor: isOutOfStock || (isFrame && !selectedSize) ? "#ccc" : "#1f2730",
                    color: "#f2d5bd",
                    border: "none",
                    padding: "18px 20px",
                    fontSize: "1.1em",
                    fontWeight: "600",
                    borderRadius: "50px",
                    cursor: isOutOfStock || (isFrame && !selectedSize) ? "not-allowed" : "pointer",
                    transition: "all 0.3s ease",
                    boxShadow: "0 4px 15px rgba(31, 39, 48, 0.3)"
                  }}
                  disabled={isOutOfStock || (isFrame && !selectedSize)}
                  onClick={addToCart}
                  onMouseEnter={(e) => {
                    if (!(isOutOfStock || (isFrame && !selectedSize))) {
                      e.target.style.backgroundColor = "#16191f";
                      e.target.style.boxShadow = "0 6px 20px rgba(31, 39, 48, 0.4)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!(isOutOfStock || (isFrame && !selectedSize))) {
                      e.target.style.backgroundColor = "#1f2730";
                      e.target.style.boxShadow = "0 4px 15px rgba(31, 39, 48, 0.3)";
                    }
                  }}
                >
                  {isOutOfStock
                    ? "Out of stock"
                    : isFrame && !selectedSize
                    ? "Select size first"
                    : "Add to cart"}
                </button>

                <button
                  type="button"
                  onClick={addToWishlistHandler}
                  title="Add to wishlist"
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: "50%",
                    border: "none",
                    backgroundColor: "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                    cursor: "pointer"
                  }}
                >
                  <FaRegHeart style={{ color: "#ff4d6d", fontSize: "20px" }} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* You May Also Like Section */}
      <YouMayAlsoLike currentProduct={product} />

    </div>
  );
}

// ====== YOU MAY ALSO LIKE COMPONENT ======
function YouMayAlsoLike({ currentProduct }) {
  const { products } = useGlobal();
  const [relatedProducts, setRelatedProducts] = useState([]);
  const sliderRef = useRef(null);

  // Click tracking function for related products
  const trackProductClick = async (product) => {
    console.log("🔥 YouMayAlsoLike trackProductClick called for product:", product.name);
    try {
      const clickData = {
        productName: product.name,
        productId: product.id,
        username: auth.currentUser ? auth.currentUser.email : "Anonymous",
        timestamp: new Date().toISOString(),
        page: window.location.pathname
      };

      console.log("📝 YouMayAlsoLike attempting to add click data:", clickData);
      await addDoc(collection(db, "clicks"), clickData);
      console.log("✅ YouMayAlsoLike click tracked successfully:", clickData);
    } catch (error) {
      console.error("❌ YouMayAlsoLike error tracking click:", error);
      console.error("Error details:", error.message);
    }
  };

  const scrollswiper = (amount) => {
    if (!sliderRef.current) return;
    sliderRef.current.scrollBy({ left: amount, behavior: "smooth" });
  };

  // Memoize related products when currentProduct changes
  useEffect(() => {
    const filtered = products
      .filter(p => 
        p.category === currentProduct.category && 
        p.id !== currentProduct.id
      )
      .sort(() => Math.random() - 0.5)
      .slice(0, 6);
    
    setRelatedProducts(filtered);
  }, [currentProduct, products]);

  if (relatedProducts.length === 0) {
    return null;
  }

  return (
    <section className="mt-5 mb-5">
      <div className="maxw slider" style={{flexDirection:'column'}}>
        <div className="w-100 d-flex justify-content-between px-4 maxw mb-3">
          <h2 className="fs-1" style={{fontWeight: "600", color: "#1f2730"}}>
            You May Also Like:
          </h2>

          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <button className="pagination" onClick={() => scrollswiper(-1000)}>
                <IoIosArrowBack />
              </button>
              <button className="pagination" onClick={() => scrollswiper(1000)}>
                <IoIosArrowForward />
              </button>
            </div>
          </div>
        </div>

        <div className="Productslider" ref={sliderRef}>
          {relatedProducts.map((product) => (
            <div 
              key={product.id} 
              className="product"
            >
              <div className="imgcontainer d-flex align-items-center justify-content-center">
                <Link 
                  to={`/products/${slugify(product.name)}`}
                  className='navlink'
                  onClick={(e) => {
                    trackProductClick(product);
                    // Don't prevent default - allow navigation
                  }}
                >
                  <img src={product.imageUrl || product.image} alt={product.name} />
                </Link>
              </div>
              <div className="w-100">
                <Link 
                  to={`/products/${slugify(product.name)}`}
                  className='navlink'
                  onClick={(e) => {
                    trackProductClick(product);
                    // Don't prevent default - allow navigation
                  }}
                >
                  <h5 style={{fontWeight: "600", marginTop: "10px"}}>{product.name}</h5>
                </Link>
                <p style={{fontSize: "18px", fontWeight: "600", color: "#ff6344"}}>
                  {product.category === "Frames" ? 
                    `From LE ${product.smallframeprice || product.smallFrameprice || 0}.00`
                    : `LE ${product.price || 0}.00`
                  }
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

